import type { AdEventName } from '../cms/tracking'
import { state } from '../state'
import { MONTILLA_AD_PARAM } from '../constants'
import { buildAdLinkId, type AdLinkParts } from '../utils/ad-link'
import { getAdAttributionStore } from '../stores/ad-attribution-store'
import type { AdDecision, AdExplainOptions, AdOffer, AdRenderOptions, AdSlotHandle, AdSlotInspection, AdsExplanation } from '../types/ads-render'

/**
 * MontillaAds public API (`montilla.ads.*`) — the client-side foundation for
 * our own ads tool. Generation (build/decorate links) lives here; capture is
 * wired into the session pipeline (load/page/faro) via {@link getAdSessionAttrs}.
 */

/** Options for generating an ad link id. `app` defaults to the loaded config. */
export interface AdLinkOptions {
  /** Overrides `config.app.name` (rarely needed). */
  app?: string
  campaign?: string
  creative?: string
}

/** Resolve the effective param name (config override → default `mad`). */
function adParamName(): string {
  return state.config?.ads?.param ?? MONTILLA_AD_PARAM
}

/**
 * Whether the MontillaAds *feature* (ad rendering) is turned on for this client.
 * This is an explicit opt-in — default OFF — and gates the ads product, NOT the
 * attribution capture below (which is always on; montilla params are detected
 * automatically regardless of this flag).
 */
function adsFeatureEnabled(): boolean {
  return state.config?.ads?.enabled === true
}

/** Resolve the app slug source: explicit override → loaded app name. */
function resolveApp(explicit?: string): string {
  const app = explicit ?? state.config?.app?.name
  if (!app) {
    throw new Error('[montilla] ads.linkId: no app name — pass { app } or call montilla.load() with app.name first')
  }
  return app
}

/**
 * Generate an opaque, trackable ad link id derived from the app name (+
 * optional campaign/creative). Each call is unique (nonce).
 * @example montilla.ads.linkId({ campaign: 'black-friday' })
 *          // → 'mad.quero-bolsa.black-friday._.k3f9ab'
 */
function linkId(options: AdLinkOptions = {}): string {
  return buildAdLinkId({
    app: resolveApp(options.app),
    campaign: options.campaign,
    creative: options.creative,
  })
}

/**
 * Append a generated ad link id to a URL as the `mad` param (config-aware).
 * Returns the decorated URL string. Existing params are preserved.
 * @example montilla.ads.decorate('https://x.com/o', { campaign: 'bf' })
 */
function decorate(url: string, options: AdLinkOptions = {}): string {
  const id = linkId(options)
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined)
    u.searchParams.set(adParamName(), id)
    return u.toString()
  } catch {
    // Not a parseable URL — append manually as a best effort.
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}${adParamName()}=${encodeURIComponent(id)}`
  }
}

/**
 * The persisted first-touch attribution for this visitor, decoded — or null.
 * @example montilla.ads.getAttribution()?.campaign
 */
function getAttribution(): (AdLinkParts & { id: string }) | null {
  return getAdAttributionStore().peek()
}

/** Whether the MontillaAds feature (ad rendering) is active for this client. */
function isEnabled(): boolean {
  return adsFeatureEnabled()
}

/** Bridge server-stamped ad events into the regular tracking pipeline. */
async function trackAdEvent(event: AdEventName, attributes: Record<string, string>): Promise<void> {
  const { montilla } = await import('../index')
  void montilla.track(event, attributes)
}
function emitAdEvent(event: AdEventName, attributes: Record<string, string>): void {
  void trackAdEvent(event, attributes)
}

/**
 * Render an ad slate into a target element for a registered slot.
 * The render module is imported lazily — pages that never show an ad pay
 * nothing. Requires the ads feature to be enabled for the client.
 * @example montilla.ads.render('#promo', { slot: 'home-hero' })
 */
async function render(
  target: string | HTMLElement,
  options: AdRenderOptions,
): Promise<AdSlotHandle | null> {
  if (!adsFeatureEnabled()) return null
  const mod = await import('../cms/render')
  return mod.render(target, options, emitAdEvent)
}

/**
 * Ligar impressão e clique a cards que NÃO foram pintados pelo SDK.
 *
 * Existe para os dois casos em que o slate não é nosso: `renderMode: 'custom'`
 * (headless — o host monta a própria UI a partir de `decision + offers`) e o
 * slot vindo do servidor, onde o markup já está no HTML e só falta o
 * comportamento. Nos dois, o que não pode mudar é a contagem: a IMPRESSÃO é do
 * cliente, sempre — anúncio pintado no servidor não foi visto (RFC §6.5).
 *
 * `cards[i]` casa com `offers[i]`, como no slate. Devolve o disposer.
 *
 * O módulo de tracking entra por import LAZY, como o render: página que nunca
 * mostra anúncio não paga por ele. Por isso o disposer devolvido é um envelope —
 * ele já existe antes de o módulo chegar, e cancela a observação mesmo se for
 * chamado nesse intervalo.
 */
function observe(
  cards: HTMLElement[],
  ctx: { decision: AdDecision, offers: AdOffer[], impressions?: boolean },
): () => void {
  let dispose: (() => void) | null = null
  let disposed = false
  void import('../cms/tracking').then(({ trackSlate }) => {
    if (disposed) return
    dispose = trackSlate(cards, ctx.offers, ctx.decision, emitAdEvent, {
      impressions: ctx.impressions,
    })
  })
  return () => {
    disposed = true
    dispose?.()
    dispose = null
  }
}

/** Unmount a slot previously mounted by {@link render} (or auto-scan). */
async function unmount(target: string | HTMLElement): Promise<void> {
  const mod = await import('../cms/render')
  mod.unmount(target)
}

/**
 * Render every `[data-montilla-slot]` slot on the page. Called automatically
 * by `load()` when the ads feature is on; exposed for SPAs that add slots
 * after navigation.
 */
async function scan(): Promise<AdSlotHandle[]> {
  if (!adsFeatureEnabled()) return []
  const mod = await import('../cms/render')
  return mod.scan(emitAdEvent)
}

/**
 * Liga o debug de slots e repinta a página. Dois modos:
 *
 * - `'asset'` (padrão) — render real + contorno na área, e a moldura rotulada
 *   só onde não veio peça. Responde "o que foi entregue e onde faltou".
 * - `'box'` — só a moldura em todo slot, sem rede. Responde "que área cada
 *   slot ocupa", inclusive os que dariam no-fill.
 *
 * Persiste em localStorage, então sobrevive a reload (é a mesma chave que a
 * extensão manipula).
 * @example montilla.ads.debug()         // asset + contorno
 * @example montilla.ads.debug('asset-box') // peça com o nome do slot por cima
 * @example montilla.ads.debug('box')    // só as molduras
 * @example montilla.ads.debug(false)    // desliga
 */
async function debug(on: boolean | 'box' | 'asset' | 'asset-box' = true): Promise<AdSlotHandle[]> {
  const mod = await import('../cms/render')
  try {
    if (on) localStorage.setItem(mod.AD_DEBUG_KEY, on === true ? 'asset' : on)
    else localStorage.removeItem(mod.AD_DEBUG_KEY)
  } catch { /* storage blocked — fall back to the load() config flag */ }
  if (!adsFeatureEnabled()) return []
  return mod.scan(emitAdEvent)
}

/**
 * Set page-level context and re-scan, so a client-side change (a tab switch on
 * the SAME URL) can re-target ads without a navigation. The context is merged
 * into every slot's request: `tags` feeds the `tags` targeting dimension, and
 * `routePattern` feeds the `page` dimension (the host's route, e.g.
 * `/cursos/:slug` — the server never derives it from the URL).
 *
 * `rescan: false` for the caller who is about to scan anyway — a framework
 * wrapper setting the route on every navigation would otherwise spend TWO `/ads`
 * requests per page: one here, one in its own scan.
 *
 * @example montilla.ads.setContext({ tags: ['undergraduate'] })
 * @example montilla.ads.setContext({ routePattern: '/cursos/:slug' }, { rescan: false })
 */
async function setContext(
  ctx: { tags?: string[], routePattern?: string },
  options?: { rescan?: boolean },
): Promise<AdSlotHandle[]> {
  const mod = await import('../cms/render')
  mod.setPageContext(ctx)
  if (options?.rescan === false || !adsFeatureEnabled()) return []
  return mod.scan(emitAdEvent)
}

/**
 * O que o SDK sabe de cada slot `[data-montilla-slot]` da página: a decisão que
 * recebeu — do servidor ou do `/ads` —, o motivo do vazio e as ofertas
 * servidas, com os ids de rastreio. Só leitura, sem rede.
 *
 * Existe para quem inspeciona a página (a extensão, o devtools): a publicação
 * que preencheu o slot não aparece em lugar nenhum do DOM.
 * @example (await montilla.ads.inspect())[0]?.decision?.adSetName
 */
async function inspect(): Promise<AdSlotInspection[]> {
  const mod = await import('../cms/render')
  return mod.inspect()
}

/**
 * A disputa de um slot: o `/ads` em dry-run com a mesma pergunta que produziu a
 * peça da tela, e cada candidato com a razão do motor. `preview` pergunta como
 * se o preview estivesse ligado — é o que lista o que dá para forçar.
 * @example await montilla.ads.explain('[data-montilla-slot="home-hero"]', { preview: true })
 */
async function explain(target: string | HTMLElement, options?: AdExplainOptions): Promise<AdsExplanation | null> {
  if (!adsFeatureEnabled()) return null
  const mod = await import('../cms/render')
  return mod.explain(target, options)
}

export const ads = { linkId, decorate, getAttribution, isEnabled, render, observe, unmount, scan, debug, setContext, inspect, explain }

/**
 * Resolve `montilla_ad_*` session attributes for the current URL — first-touch,
 * persisted. Called from the session builders (load/page/faro) alongside
 * `getUtmParams`, so the montilla ad params ride on every event automatically.
 *
 * ALWAYS ON: detecting montilla params is unconditional and independent of
 * `config.ads.enabled` (that flag gates the ads *feature*, not this capture).
 * Returns {} only when no valid `mad` first-touch exists.
 */
export function getAdSessionAttrs(searchParams: URLSearchParams): Record<string, string> {
  return getAdAttributionStore().sessionAttrs(
    searchParams,
    adParamName(),
    () => new Date().toISOString(),
  )
}
